import Vue from 'vue';
import VueRouter from 'vue-router';

import SigninPage from './components/auth/Login.vue';
import PageNotFound from './components/system/errors.vue';
import Campaigns from './components/dashboard/campaigns/index.vue';
import EmailEvents from './components/dashboard/emailevents/index.vue';
import FeaturedHistory from './components/dashboard/featuredhistory/index.vue';
import ClientAnalyticsDirectory from './components/dashboard/clientanalytics/directory/index.vue';
import ClientAnalyticsProperty from './components/dashboard/clientanalytics/property/index.vue';
import PropertyEnquiry from './components/dashboard/propertyenquiry/index.vue';
import DirectoryEnquiry from './components/dashboard/directoryenquiry/index.vue';
import Analytics from './components/dashboard/analytics/index.vue';
import Templates from './components/dashboard/templates/index.vue';
import Features from './components/dashboard/features/index.vue';
import AltNames from './components/dashboard/altnames/index.vue';
import Unauthorized from './components/dashboard/unauthorized/index.vue';
import Dashboard from './components/dashboard/index.vue';
import PropertyQCDashboard from './components/property-qc/Dashboard.vue';
import Locations from './components/locations/Dashboard.vue';
import LocationEdit from './components/locations/LocationEdit.vue';
import LocationCreate from './components/locations/LocationCreate.vue';
import LocationsVerification from './components/locations/Verification.vue';
import ShapeViewer from './components/locations/ShapeViewer.vue';
import UtilitiesDashboard from './components/dashboard/utilities/Dashboard.vue';
import XmlImport  from './components/dashboard/xmlimport/Dashboard.vue';
import XmlDashboard  from './components/dashboard/xmlimport/Index.vue';
import Rules  from './components/dashboard/xmlimport/RulesDashboard.vue';
import Ftp  from './components/dashboard/xmlimport/ftp.vue';
import XmlAlternativeNames from './components/dashboard/xmlimport/AlternativeNamesDashboard.vue';
import NarratoSyncDashboard from './components/dashboard/narratosync/Dashboard.vue';
import ImportReport from './components/dashboard/xmlimport/ImportReport.vue';
import UnindexedURLs from './components/dashboard/gsc/unindexed-urls/ViewURLs.vue';
import InspectionResults from './components/dashboard/gsc/unindexed-urls/ViewInspectionResults.vue';

import store from './store';
import { hasPolicyWithAnyAction, hasPolicyWithAllActions } from './auth';

Vue.use( VueRouter );

const policyCheckAny = ( ...validActions ) => {
    return function( to, from, next ) {
        if ( !store.state.user ) {
            next( '/?returnUrl=' + to.fullPath );
            return;
        }

        if ( !hasPolicyWithAnyAction( store.state.user, ...validActions ) ) {
            next( '/unauthorized' );
            return;
        }
        tokenCheck( to, from, next );
    };
};

const policyCheckAll = ( ...validActions ) => {
    return function( to, from, next ) {
        if ( !store.state.user ) {
            next( '/?returnUrl=' + to.fullPath );
            return;
        }

        if ( !hasPolicyWithAllActions( store.state.user, ...validActions ) ) {
            next( '/unauthorized' );
            return;
        }
        tokenCheck( to, from, next );
    };
};

const tokenCheck = ( to, from, next ) => {
    if ( localStorage.getItem( 'token' ) ) {
        next();
    } else {
        next( '/' );
    }
};

const routes = [
    {
        path: '/',
        component: SigninPage,
        beforeEnter( to, from, next ) {
            if ( localStorage.getItem( 'token' ) ) {
                next( '/' );
            } else {
                next();
            }
        }
    },
    {
        path: '/logout',
        beforeEnter( to, from, next ) {
            localStorage.removeItem( 'token' );
            localStorage.removeItem( 'refreshToken' );
            localStorage.removeItem( 'useremail' );
            localStorage.removeItem( 'username' );
            next( '/' );
        }
    },
    {
        path: '/unauthorized',
        component: Unauthorized
    },
    {
        path: '/dashboard',
        component: Dashboard
    },
    {
        path: '/utilities',
        component: UtilitiesDashboard
    },
    {
        path: '/narrato-sync',
        component: NarratoSyncDashboard
    },
    {
        path: '/templates',
        component: Templates,
        beforeEnter: policyCheckAny( 'emailtemplate:*' )
    },
    {
        path: '/campaigns',
        component: Campaigns,
        beforeEnter: policyCheckAny( 'campaign:*' )
    },
    {
        path: '/emailevents',
        component: EmailEvents,
        beforeEnter: policyCheckAny( 'emailevent:*' )
    },
    {
        path: '/featuredhistory',
        component: FeaturedHistory,
        beforeEnter: policyCheckAny( 'featuredhistory:*' )
    },
    {
        path: '/analytics',
        component: Analytics,
        beforeEnter: policyCheckAny( 'analytics:*' )
    },
    {
        path: '/features',
        component: Features,
        beforeEnter: policyCheckAll( 'featuremapping:*', 'feature:*' )
    },
    {
        path: '/locations',
        component: Locations,
        beforeEnter: tokenCheck
    },
    {
        path: '/locations/create',
        component: LocationCreate,
        beforeEnter: tokenCheck
    },
    {
        path: '/locations/:id(\\d+)',
        component: LocationEdit,
        beforeEnter: policyCheckAll( 'locationsmanagement:*' )
    },
    {
        path: '/locations/verification',
        component: LocationsVerification,
        beforeEnter: tokenCheck
    },
    {
        path: '/shape-viewer',
        component: ShapeViewer,
        beforeEnter: policyCheckAll( 'locationsmanagement:*' )
    },
    {
        path: '/altnames',
        component: AltNames,
        beforeEnter: policyCheckAll( 'locationmapping:*' )
    },
    {
        path: '/property-quality-control',
        component: PropertyQCDashboard,
        beforeEnter: policyCheckAny( 'propertyqc:*' )
    },
    {
        path: '/directoryanalytics',
        component: ClientAnalyticsDirectory,
        beforeEnter: policyCheckAny( 'clientanalytics:*' )
    },
    {
        path: '/propertyanalytics',
        component: ClientAnalyticsProperty,
        beforeEnter: policyCheckAny( 'clientanalytics:*' )
    },
    {
        path: '/propertyenquiry',
        component: PropertyEnquiry,
        beforeEnter: policyCheckAny( 'enquiry:*', 'enquiry:property:*', 'enquiry:property:read:*', 'enquiry:property:read:non-spam' )
    },
    {
        path: '/directoryenquiry',
        component: DirectoryEnquiry,
        beforeEnter: policyCheckAny( 'enquiry:*', 'enquiry:directory:*', 'enquiry:directory:read:*', 'enquiry:directory:read:non-spam' )
    },
    {
        path: '/xmldashboard',
        component: XmlDashboard,
        beforeEnter: policyCheckAll( 'xml-import:*', 'xml-import-rule:*', 'xml-alternative-name:*' )
    },
    {
        path: '/xmlimport',
        component: XmlImport,
        beforeEnter: policyCheckAll( 'xml-import:*', 'xml-import-rule:*', 'xml-alternative-name:*' )
    },
    {
        path: '/xmlimportrules',
        component: Rules,
        beforeEnter: policyCheckAll( 'xml-import:*', 'xml-import-rule:*', 'xml-alternative-name:*' )
    },
    {
        path: '/ftp',
        component: Ftp,
        beforeEnter: policyCheckAll( 'xml-import:*' )
    },
    {
        path: '/xmlalternativenames',
        component: XmlAlternativeNames,
        beforeEnter: policyCheckAll( 'xml-import:*', 'xml-import-rule:*', 'xml-alternative-name:*' )
    },
    {
        path: '/xmlimportreport',
        component: ImportReport,
        beforeEnter: policyCheckAny( 'xml-import:*', 'xml-import:read' )
    },
    {
        path: '/gsc/unindexed-urls',
        component: UnindexedURLs,
        beforeEnter: policyCheckAll( 'unindexed-urls:*' )
    },
    {
        path: '/gsc/inspection-results',
        component: InspectionResults,
        beforeEnter: policyCheckAll( 'unindexed-urls:*' )
    },
    {
        path: '*',
        component: PageNotFound
    }
];

export default new VueRouter( { mode: 'history', routes } );



// WEBPACK FOOTER //
// ./src/router.js